import { useState, useCallback } from "react";

/**
 * Common validation rules for forms
 */
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/,
    patternMessage:
      "Name can only contain letters, spaces, hyphens, and apostrophes",
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    patternMessage: "Please enter a valid email address",
  },
  phone: {
    required: false,
    pattern: /^[+]?[\d\s\-()]{7,20}$/,
    patternMessage: "Please enter a valid phone number",
  },
  message: {
    required: true,
    minLength: 10,
    maxLength: 1000,
  },
  password: {
    required: true,
    minLength: 6,
    maxLength: 128,
  },
  url: {
    required: false,
    pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
    patternMessage: "Please enter a valid URL",
  },
};

/**
 * Custom hook for form validation and error handling
 * Replaces repeated form state management patterns
 */
export const useFormValidation = (initialState = {}, validationRules = {}) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear field error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors]
  );

  const setFieldError = useCallback((fieldName, errorMessage) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: errorMessage,
    }));
  }, []);

  const setGeneralError = useCallback((errorMessage) => {
    setErrors({ general: errorMessage });
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
    setIsLoading(false);
    setIsSubmitting(false);
  }, [initialState]);

  // Update field helper
  const updateField = useCallback((fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear field error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  }, [errors]);

  // Validate a single field
  const validateField = useCallback((fieldName, value, rules, formData = {}) => {
    if (!rules) return true;

    // Check required
    if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      return rules.required === true ? 'This field is required' : (typeof rules.required === 'string' ? rules.required : 'This field is required');
    }

    // If field is not required and empty, skip other validations
    const isEmpty = !value || (typeof value === 'string' && value.trim() === '');
    if (!rules.required && isEmpty) {
      return true;
    }

    // Check custom validate function first (most specific validation)
    if (rules.validate && typeof rules.validate === 'function') {
      const validateResult = rules.validate(value, formData);
      if (validateResult !== true) {
        return validateResult; // Return error message
      }
    }

    // Check minLength (only if value is provided)
    if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
      return `Must be at least ${rules.minLength} characters`;
    }

    // Check maxLength
    if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
      return `Must be less than ${rules.maxLength} characters`;
    }

    // Check pattern
    if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
      return rules.patternMessage || 'Invalid format';
    }

    return true;
  }, []);

  // Validate entire form
  const validateForm = useCallback(() => {
    const newErrors = {};

    Object.keys(validationRules).forEach((field) => {
      const rules = validationRules[field];
      const value = formData[field];
      const error = validateField(field, value, rules, formData);
      
      if (error !== true) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validationRules, validateField]);

  // Handle form submission with validation
  const handleSubmit = useCallback(async (submitCallback) => {
    // Validate form
    if (!validateForm()) {
      return false;
    }

    setIsSubmitting(true);
    try {
      await submitCallback(formData);
      return true;
    } catch (error) {
      console.error('Form submission error:', error);
      setGeneralError(error.message || 'An error occurred while submitting the form');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, setGeneralError]);

  return {
    formData,
    setFormData,
    errors,
    isLoading,
    setIsLoading,
    isSubmitting,
    handleInputChange,
    updateField,
    setFieldError,
    setGeneralError,
    clearErrors,
    resetForm,
    validateForm,
    handleSubmit,
  };
};
